<template>
  <div id="pipelines" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between align-center">
      <h1 class="text-xl text_color font-semibold">Pipelines</h1>
      <a-button class="primary_medium" @click="showPipelineModal('add')"
        >Create Pipeline</a-button
      >
    </div>

    <CustomTable
      :headers="pipelineHeaders"
      :data="pipelines"
      :pagination="pagination"
      :show-actions-column="true"
      :actions="actions"
      @update:pagination="handlePageChange"
    />

    <add-pipeline
      :drawerVisible="pipelineModalVisible"
      :is-editing="isPipelineEditing"
      :pipeline-id="pipelineToEdit"
      :show-weighted-stats="showWeightedStats"
      @update:modelValue="pipelineModalVisible = false"
      @pipeline-saved="onEditPipeline"
      @weighted-stats-toggled="(val) => showWeightedStats = val"
    />

    <CustomModal
      v-model:visible="deletePipelineModalVisible"
      title="Delete Pipeline"
      width="32vw"
    >
      <span>Are you sure you want to delete this pipeline?</span>
      <template #footer>
        <a-button class="primary_medium" @click="deletePipeline"
          >Confirm
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import CustomModal from "@/src/components/CustomModal.vue";

import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import AddPipeline from "@/src/views/pipelines/AddPipeline.vue";
import { imageNames } from "@/src/constants/imageConstants.js";

const store = useStore();

const pipelines = ref([]);
const showWeightedStats = ref(false);
const pipelineModalVisible = ref(false);
const deletePipelineModalVisible = ref(false);
const isPipelineEditing = ref(false);
const pipelineToDelete = ref(null);
const pipelineToEdit = ref(null);

const pagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
});

const pipelineHeaders = [
  { label: "Pipeline Name", key: "name", width: 4 },
  { label: "No. of stages", key: "stages_count", width: 4 },
  { label: "Deals", key: "deal_count", width: 4 },
];

const actions = [
  {
    label: "Edit",
    handler: (row) => showPipelineModal("edit", row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => showDeleteConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const fetchPipelines = async () => {
  try {
    // Use store to fetch pipelines (uses cache if available)
    const response = await store.dispatch("pipelines/fetchPipelines");
    if (response.success) {
      pipelines.value = response.pipelines;
      showWeightedStats.value = response.show_weighted_stats || false;
    } else {
      message.error("Failed to fetch pipelines");
    }
  } catch (error) {
    console.error("Error fetching pipelines:", error);
    message.error("An error occurred while fetching pipelines");
  }
};

const showDeleteConfirm = (record) => {
  deletePipelineModalVisible.value = true;
  pipelineToDelete.value = record;
};

const handlePageChange = (newPagination) => {
  pagination.value = newPagination;
  fetchPipelines();
};

const showPipelineModal = (mode, pipeline = null) => {
  if (mode === "edit" && pipeline) {
    pipelineToEdit.value = pipeline.id;
    isPipelineEditing.value = true;
  } else {
    isPipelineEditing.value = false;
  }
  pipelineModalVisible.value = true;
};

const onEditPipeline = async (data) => {
  pipelineModalVisible.value = false;

  if (isPipelineEditing.value) {
    await store.dispatch("pipelines/pipelineUpdated", {
      pipelineId: pipelineToEdit.value,
    });
  } else {
    await store.dispatch("pipelines/pipelineCreated");
  }

  await fetchPipelines();
};

const deletePipeline = async () => {
  if (!pipelineToDelete.value.is_deletable) {
    message.error("Cannot delete default pipeline");
    deletePipelineModalVisible.value = false;
    return;
  }

  try {
    const response = await DashboardService.deletePipeline(
      pipelineToDelete.value.id,
    );
    if (response.success) {
      message.success("Pipeline deleted successfully");

      await store.dispatch("pipelines/pipelineDeleted", {
        pipelineId: pipelineToDelete.value.id,
      });

      await fetchPipelines();
    } else {
      message.error(response.error_message);
    }
  } catch (error) {
    console.error("Error deleting Pipeline:", error);
    message.error("An error occurred while deleting the Pipeline");
  } finally {
    deletePipelineModalVisible.value = false;
  }
};

onMounted(() => {
  fetchPipelines();
});
</script>
